import PropTypes from 'prop-types';
import React from 'react';
import { Form, TooltipHelp, TranslateDropdown } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { ToggleInheritedInput } from './InheritedInput';
import EditorComponent from '../../../../nacre/components/editorComponent';
import Styles from '../../../../styles/rate/rateCreation.module.css';

export function RateNameInput({
    event,
    rate,
    defaultLanguage,
    languageOptions,
    form,
    onChangeFormObject,
    setNameModified,
    handleTranslations = () => {},
    handleLanguage = () => {}
}) {
    const { t } = useTranslation();
    const [selectedNameLanguage, setSelectedNameLanguage] = React.useState(defaultLanguage);

    const rateNameValue = React.useMemo(() => {
        if (rate?.rate_template && !form.name) {
            return rate?.rate_template.name;
        }
        return !Object.keys(form.translationsName) || selectedNameLanguage === defaultLanguage ? form.name : form.translationsName[selectedNameLanguage];
    }, [rate, defaultLanguage, form, selectedNameLanguage]);

    const [valueLength, setValueLength] = React.useState(rateNameValue?.length || 0);

    const handleChange = React.useCallback(
        ({ target: { value } }) => {
            handleTranslations(value, 'name', selectedNameLanguage);
            setValueLength(value.length);
        },
        [selectedNameLanguage]
    );

    const maxLength = 60;

    return (
        <>
            <Form.Label>
                <div className={Styles['rate-name-input']}>
                    <span>
                        {t(`ticket.rates.infos.name`)}
                        <TooltipHelp title={t('ticket.rates.infos.nameTooltip')} />
                    </span>
                    <span className={Styles['rate-name-input']}>
                        {form.name && event && (
                            <TranslateDropdown
                                defaultValue={defaultLanguage}
                                languages={languageOptions}
                                onChange={value => {
                                    setSelectedNameLanguage(value);
                                    handleLanguage(value, 'name');
                                }}
                                selected={Object.keys(form.translationsName)}
                            />
                        )}
                        {rate?.rate_template && (
                            <ToggleInheritedInput
                                rate={rate}
                                form={form}
                                onChange={form => {
                                    onChangeFormObject({ ...form }, true);
                                    setNameModified(true);
                                }}
                                property="name"
                            />
                        )}
                    </span>
                </div>
            </Form.Label>

            <Form.Input
                required
                name="name"
                placeholder={t(`ticket.rates.infos.placeholderName`)}
                value={rateNameValue}
                disabled={rate?.rate_template && !form.name}
                onChange={handleChange}
            />
            <span className={Styles['input-length']}>
                <span>
                    {valueLength || 0}
                    {`/${maxLength}`}
                </span>
                {valueLength > 60 && <span className={Styles['input-length-message']}>{t('ticket.rates.infos.name-too-long')}</span>}
            </span>
        </>
    );
}

RateNameInput.propTypes = {
    event: PropTypes.object,
    rate: PropTypes.object,
    defaultLanguage: PropTypes.string.isRequired,
    languageOptions: PropTypes.array.isRequired,
    form: PropTypes.object,
    onChangeFormObject: PropTypes.func,
    setNameModified: PropTypes.func,
    handleTranslations: PropTypes.func,
    handleLanguage: PropTypes.func
};

export function RateDescriptionInput({ event, rate, defaultLanguage, languageOptions, form, handleTranslations = () => {}, handleLanguage = () => {} }) {
    const { t } = useTranslation();
    const [selectedDescriptionLanguage, setSelectedDescriptionLanguage] = React.useState(defaultLanguage);

    const rateDescriptionValue = React.useMemo(() => {
        if (rate?.rate_template && !form.description) {
            return rate?.rate_template.description;
        }
        return !Object.keys(form.translationsDescription) ? form.description : form.translationsDescription[selectedDescriptionLanguage];
    }, [rate, defaultLanguage, form, selectedDescriptionLanguage]);

    return (
        <>
            <Form.Label>
                <div className={Styles['rate-name-input']}>
                    <span>
                        {t('ticket.rates.infos.labelDescription')}
                        <TooltipHelp title={t('ticket.rates.infos.descriptionTooltip')} />
                        <span className={Styles['optional']}>{t('ticket.common.labelOptional')}</span>
                    </span>
                    {form.name && event && (
                        <TranslateDropdown
                            defaultValue={defaultLanguage}
                            languages={languageOptions}
                            onChange={value => {
                                setSelectedDescriptionLanguage(value);
                                handleLanguage(value, 'description');
                            }}
                            selected={Object.keys(form.translationsDescription)}
                        />
                    )}
                </div>
            </Form.Label>
            <Form.Sublabel label={t('ticket.rates.infos.sublabelDescription')} />

            <EditorComponent
                description={rateDescriptionValue}
                onChange={(content, editor) => handleTranslations(content, 'description', selectedDescriptionLanguage)}
                placeholder={t('ticket.rates.infos.placeholderDescription')}
                height={300}
                menubar={false}
            />
        </>
    );
}

RateDescriptionInput.propTypes = {
    rate: PropTypes.object,
    defaultLanguage: PropTypes.string.isRequired,
    languageOptions: PropTypes.array.isRequired,
    form: PropTypes.object,
    onChangeFormObject: PropTypes.func,
    handleTranslations: PropTypes.func,
    handleLanguage: PropTypes.func
};
